import React, { useState, useCallback } from "react";

const STATUS_LABELS = {
  saved: "Saved",
  saving: "Saving...",
  unsaved: "Save now",
  error: "Save error",
  local: "Draft -- delete a rig to save",
};

function ShareLinkButton({ slug }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/rigs/${slug}`;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  return (
    <button
      className="board-toolbar-btn"
      onClick={handleCopy}
      type="button"
      aria-label="Share link"
    >
      {copied ? "Copied!" : "Share link"}
    </button>
  );
}

function SlugField({ slug, rigId, onUpdateSlug }) {
  const [value, setValue] = useState(slug || "");

  const handleBlur = () => {
    if (value !== slug) {
      onUpdateSlug(rigId, value);
    }
  };

  return (
    <div className="board-toolbar-slug">
      <label htmlFor="board-toolbar-slug">Slug</label>
      <input
        id="board-toolbar-slug"
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        className="board-toolbar-slug-input"
      />
    </div>
  );
}

export default function BoardToolbar({
  authenticated = false,
  currentRig,
  atLimit,
  saveStatus,
  onDuplicate,
  onDelete,
  onTogglePrivate,
  onUpdateSlug,
  isAdmin = false,
  saveNow,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  showSignup = false,
  hasCustomDevices = false,
  nodes,
  edges,
  trackEvent,
  devMode = false,
  currentRigSlug,
}) {
  const privateLocked = hasCustomDevices && currentRig?.private;
  const privateLockMessage = "This rig must stay private while it contains a custom device.";

  return (
    <div className="board-toolbar">
      {authenticated ? (
        saveStatus === "unsaved" ? (
          <button
            className="board-toolbar-btn board-toolbar-btn--save"
            onClick={saveNow}
            type="button"
            aria-label="Save now"
          >
            Save now
          </button>
        ) : (
          <span className={`pill ${saveStatus === "saved" ? "sage" : saveStatus === "saving" ? "amber" : "red"}`}>
            {STATUS_LABELS[saveStatus] || saveStatus}
          </span>
        )
      ) : showSignup ? (
        <a
          href="/users/sign_up"
          className="board-toolbar-btn board-toolbar-btn--signup"
          onClick={() => trackEvent("signup_prompt_clicked", {
            device_count: nodes.length,
            connection_count: edges.length,
          })}
        >
          Sign up to save
        </a>
      ) : null}

      {canUndo && (
        <button
          className="board-toolbar-btn"
          onClick={onUndo}
          type="button"
          title="Undo (Ctrl+Z)"
          aria-label="Undo"
        >
          Undo
        </button>
      )}
      {canRedo && (
        <button
          className="board-toolbar-btn"
          onClick={onRedo}
          type="button"
          title="Redo (Ctrl+Shift+Z)"
          aria-label="Redo"
        >
          Redo
        </button>
      )}

      {devMode && currentRigSlug && (
        <a
          className="board-toolbar-btn"
          href={`/rigs/${currentRigSlug}/og`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View OG
        </a>
      )}

      {authenticated && currentRig && (
        <>
          <span className="board-toolbar-spacer" />

          {!currentRig.draft && (
            <label className="board-toolbar-private" title={privateLocked ? privateLockMessage : undefined}>
              <input
                type="checkbox"
                checked={currentRig.private || false}
                disabled={privateLocked}
                onChange={() => onTogglePrivate(currentRig.id, !currentRig.private)}
              />
              Private
              {privateLocked && (
                <span className="board-toolbar-private-note" role="note">
                  {privateLockMessage}
                </span>
              )}
            </label>
          )}

          {!currentRig.draft && !currentRig.private && currentRig.slug && (
            <ShareLinkButton slug={currentRig.slug} />
          )}

          {!currentRig.draft && (
            <button
              className="board-toolbar-btn"
              onClick={onDuplicate}
              disabled={atLimit}
              type="button"
              aria-label="Duplicate"
            >
              Duplicate
            </button>
          )}

          <button
            className="board-toolbar-btn board-toolbar-btn--delete"
            onClick={() => {
              if (window.confirm(`Delete "${currentRig.name}"?`)) {
                onDelete();
              }
            }}
            type="button"
            aria-label="Delete"
          >
            Delete
          </button>

          {!currentRig.draft && isAdmin && (
            <SlugField
              key={currentRig.id}
              slug={currentRig.slug}
              rigId={currentRig.id}
              onUpdateSlug={onUpdateSlug}
            />
          )}
        </>
      )}

    </div>
  );
}
